import { useQuery, useQueries, useInfiniteQuery } from "react-query";
import axios from "axios";
import { useAppSettingsContext } from "../../contexts/appSettingsContext";

export const getDataQueryFn = async (configs) => {
  try {
    const res = await axios.post(`/api/discovery/getData`, {
      configs: JSON.stringify(configs),
    });
    return res.data;
  } catch (err) {
    throw configs?.customError || err;
  }
};

const getDataQuery = (configs) => ({
  queryKey: ["contentListing", configs.contentTypes, configs],
  queryFn: () => getDataQueryFn(configs),
  ...(configs.queryOptions || {}),
});
const getUserAnalyticsQuery = ({ usersConfig }) => ({
  queryKey: ["analyticsListing", "user", usersConfig],
  queryFn: () =>
    axios
      .get(`/api/users/userAnalyticsData`, {
        params: { usersConfig: JSON.stringify(usersConfig) },
        // paramsSerializer: (params) => qs.stringify(params),
      })
      .then((res) => res.data),
  //...(configs.queryOptions || {}),
});
export const useGetUserAnalyticsQuery = ({ usersConfig }) => {
  return useQuery(
    getUserAnalyticsQuery({
      usersConfig,
    })
  );
};

// this is used only inside TagsManager, hence a few things are hardcoded
export const useGetDataQueries = ({ contentTypes }) => {
  return useQueries(
    contentTypes?.map((d) =>
      getDataQuery({
        contentTypes: [d],
        useCountDAL: true,
        findQuery: {
          kp_published_status: { $in: ["published", "draft"] },
        },
      })
    )
  );
};
export const useGetDataQueriesContentType = ({ configs }) => {
  return useQueries(
    configs?.map((d) =>
      getDataQuery({
        contentTypes: [d.contentType],
        //useCountDAL: true,
        findQuery: d.mostPublishedByQuery.findQuery,
        population: [
          {
            path: "meta.kp_contributed_by",
            model: "user",
          },
        ],
      })
    )
  );
};

export const useGetDataQuery = ({
  contentTypes,
  profileTypes,
  activeFilters,
  activeSort,
  searchTerm,
  population,
  projection,
  findQuery,
  taggedResourcesCount,
  lookupConfig,
  queryOptions,
  limit,
  facet,
  countData,
  env,
  pluginId,
  useAggregation,
  ksConfig,
  useCountDAL,
  translationConfig,
  customError,
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value;

  return useQuery(
    getDataQuery({
      contentTypes,
      profileTypes,
      activeFilters,
      activeSort,
      searchTerm,
      population,
      projection,
      findQuery,
      lookupConfig,
      taggedResourcesCount,
      queryOptions,
      limit,
      facet,
      countData,
      env,
      pluginId,
      useAggregation,
      ksConfig,
      useCountDAL,
      activeLang,
      translationConfig,
      customError,
    })
  );
};

export const useGetDataLazy = ({
  profileTypes,
  contentTypes,
  activeFilters,
  activeSort,
  searchTerm,
  population,
  projection,
  findQuery,
  lookupConfig,
  queryOptions,
  limit = 20,
  facet,
  countData,
  collectionId,
  ksConfig,
  taggedResourcesCount,
  useAggregation,
  useCountDAL,
  pluginId,
  translationConfig,
  disableLimit,
  includeAnnotationCount,
  /**
   * when being used to fetch the related content of a content type,
   * collectionId simply makes the query key more semantic and
   * guarantees uniqueness
   */
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value;

  const configs = {
    profileTypes,
    contentTypes,
    activeFilters,
    activeSort, // when loading data with skip and limit, sort can create issues if sort is applied on a non unique field, so _id will act as tie breaker and give consistent result.
    searchTerm,
    population,
    projection,
    findQuery,
    lookupConfig,
    queryOptions,
    ...(disableLimit ? {} : { limit }),
    facet,
    // countData is excluded from configs - we only fetch count on first page (skip === 0)
    ksConfig,
    taggedResourcesCount,
    useAggregation,
    useCountDAL,
    pluginId,
    activeLang,
    translationConfig,
    includeAnnotationCount,
  };

  // const str = qs.stringify(ksConfig);

  // const par = qs.parse(str,{depth:100,arrayLimit:100,plainObjects:true})

  // console.log({str,par,ksConfig})

  // console.log({filters,length:filters.length})
  return useInfiniteQuery({
    queryKey: [
      "contentListing",
      "lazy",
      configs.contentTypes,
      configs,
      collectionId,
      activeLang,
    ],
    queryFn: ({ pageParam: skip = 0 }) => {
      return axios
        .post(`/api/discovery/getData`, {
          configs: JSON.stringify({
            ...configs,
            skip,
            // Only fetch count on first page to avoid extra DB roundtrip on pagination
            countData: skip === 0 ? countData : false
          }),
        })
        .then((res) => res.data);
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.skip;
    },
    ...(queryOptions || {}),
  });
};

export const useGetGlobalSearchDataLazy = ({
  segments,
  activeSort,
  searchTerm,
  findQuery,
  activeFilters,
  limit = 20,
  ksConfig,
  enabled = true,
}) => {
  const configs = {
    segments,
    activeSort,
    searchTerm,
    findQuery,
    limit,
    ksConfig,
    activeFilters
  };

  return useInfiniteQuery({
    queryKey: [
      "globalSearchContentListing",
      "lazy",
      configs.segments,
      configs,
    ],
    queryFn: ({ pageParam: skip = 0 }) => {
      return axios
        .get(`/api/discovery/getGlobalSearchData`, {
          params: { configs: JSON.stringify({ ...configs, skip }) },
        })
        .then((res) => {
          return res.data
        });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.skip;
    },
    enabled,
  });
};

export const useGetAnnotations = ({
  activeFilters,
  activeSort,
  dataSource,
  searchConfig,
  searchTerm,
  projection,
  findQuery,
  queryOptions,
  collectionName,
  translationConfig,
  tplFilters,
  queryId,
  colorbreakDownFilters,
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value;

  const configs = {
    dataSource,
    searchConfig,
    tplFilters,
    activeFilters,
    activeSort,
    searchTerm,
    projection,
    findQuery,
    activeLang,
    translationConfig,
    collectionName,
    colorbreakDownFilters,
  };

  // Use useQuery for fetching all data at once
  return useQuery({
    queryKey: [
      "annotationListing",
      "all",
      configs.activeFilters,
      configs,
      collectionName,
      activeLang,
      queryId,
    ],
    queryFn: () => {
      return axios
        // POST: a filter set is a body, not a query string. Large selections
        // pushed the GET URL past Node's 16 KB header cap → HTTP 431.
        .post(`/api/discovery/getAnnotations`, { configs })
        .then((res) => ({
          data: res.data.data,
          count: res.data.count,
        }));
    },
    ...(queryOptions || {}),
  });
};

export const useGetAnnotationsLazy = ({
  activeFilters,
  activeSort,
  dataSource,
  searchConfig,
  searchTerm,
  projection,
  findQuery,
  limit = 20,
  queryOptions,
  collectionName,
  translationConfig,
  tplFilters,
  /**
   * Optional unique identifier to ensure query key uniqueness
   * when multiple instances of this hook are used
   */
  queryId,
  colorbreakDownFilters,
  rollupInjectProperty,
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value;

  // Prepare configuration object for the API request
  const configs = {
    dataSource,
    searchConfig,
    tplFilters,
    activeFilters,
    activeSort,
    searchTerm,
    projection,
    findQuery,
    limit,
    activeLang,
    translationConfig,
    collectionName,
    colorbreakDownFilters,
    rollupInjectProperty,
  };

  // Use React Query's useInfiniteQuery for lazy loading
  return useInfiniteQuery({
    queryKey: [
      "annotationListing",
      "lazy",
      configs.activeFilters,
      configs,
      collectionName,
      activeLang,
      queryId,
    ],
    queryFn: ({ pageParam: skip = 0 }) => {
      return axios
        // POST (see useGetAnnotations above for why)
        .post(`/api/discovery/getAnnotations`, { configs: { ...configs, skip } })
        .then((res) => res.data);
    },
    getNextPageParam: (lastPage) => {
      return lastPage.skip;
    },
    ...(queryOptions || {}),
  });
};

export const useES_CountData = ({ contentTypes, enabled, activeFilters, findQuery }) => {
  return useQuery({
    queryKey: ["ES_countData", contentTypes, activeFilters, findQuery],
    queryFn: () =>
      // POST: activeFilters as a GET query string exceeded Node's 16 KB header
      // cap on large selections → 431, and the header silently showed "N/0".
      axios
        .post("/api/esIndexes/countData", { contentTypes, activeFilters, findQuery })
        .then(res => res.data),
    enabled
  });
};
